// MobileRailSheet — the ONE off-canvas rail for phones. Every desktop side
// rail (tickets filters, calendar controls, chat conversations, documents
// browse) renders its content inside this sheet at @mobileL, so the whole
// platform answers to a single gesture: left-edge pull opens (see
// shared/functions/edgeSwipe.js), scrim tap / left-flick / Escape closes.
// The opaque sheet docks to the left edge, keeps its controls above the
// device safe areas, and owns keyboard focus only while it is open.
//
// Usage (inside an app's page/stage — the sheet gates itself to phones):
//   MobileRailSheet: {
//     stateKey: '_ticketsRailOpen',
//     railLabel: 'Filters',
//     Card: { Content: { ...the rail's existing children... } }
//   }
// Content mounts at Card.Content — the sheet root is the fixed inset:0
// overlay (scrim + card), so a root-level `Content` key would render an
// orphan div on the overlay instead. (Corrected 2026-08-02 after the
// tickets adoption caught the doc/implementation mismatch.)
// The consumer supplies Content; open/close state lives on root[stateKey]
// so the opener FAB, the edge gesture and the sheet all agree.
//
// Detail contract (per Nika "pay attention to details"): symmetric spacing
// (A padding, Z rhythm), no horizontal overflow (minWidth 0 throughout),
// overscroll contained, home-indicator + notch respected, reduced-motion
// collapses the slide to an instant swap (the global floor also covers it).

export const MobileRailSheet = {
  // Phone-only: desktop keeps its persistent rails. Reactive — a plain
  // create-time matchMedia read would leave the sheet stuck at whatever the
  // viewport was on mount (desktop window resized down = no rail at all).
  // The onCreate watcher below flips `_isPhoneViewport` on the root, so this
  // factory re-runs on the real breakpoint change.
  hide: (el, s) => {
    if (typeof window === 'undefined') return true
    const flag = s.root?._isPhoneViewport
    if (typeof flag === 'boolean') return !flag
    return !window.matchMedia('(max-width: 768px)').matches
  },
  // Function form (smbls 5a709adad+): the open/close key is a PROP, so the
  // static array can't express it. Root-state reads inside factories do NOT
  // auto-subscribe a descendant — without this the sheet's state said open
  // while the card stayed parked at translateX(-105%) (caught live 2026-08-02).
  subscribeTo: (el) => ['_isPhoneViewport', el.stateKey].filter(Boolean),

  scope: {
    syncRailFocus(el, open) {
      if (typeof document === 'undefined' || open === el.__railWasOpen) return
      el.__railWasOpen = open
      if (el.__railFocusFrame != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(el.__railFocusFrame)
        el.__railFocusFrame = null
      }
      if (open) {
        el.__railReturnFocus = document.activeElement
        const enter = () => {
          el.__railFocusFrame = null
          if (!el.__railWasOpen || !el.Card?.node) return
          el.Card.node.focus({ preventScroll: true })
        }
        if (typeof requestAnimationFrame === 'function') {
          el.__railFocusFrame = requestAnimationFrame(enter)
        } else Promise.resolve().then(enter)
      } else {
        const opener = el.__railReturnFocus
        el.__railReturnFocus = null
        // A nested dialog or a route change can own focus now. Do not take
        // it back from that surface when this rail closes underneath it.
        const active = document.activeElement
        const inside = active === el.node || el.node?.contains?.(active)
        if (inside && opener?.isConnected !== false && opener?.focus) {
          opener.focus({ preventScroll: true })
        }
      }
    },
    // One reader for the phone breakpoint, shared by the create-time seed and
    // the `onWindowResize` prop below. The media query is re-read on every
    // call, so no listener has to carry the previous answer.
    publishRailViewport(el) {
      if (typeof window === 'undefined' || !window.matchMedia) return
      const matches = !!window.matchMedia('(max-width: 768px)').matches
      const r = el.getRootState()
      if (r && r._isPhoneViewport !== matches) {
        r.update({ _isPhoneViewport: matches }, { preventFetch: true })
      }
    }
  },

  position: 'fixed',
  inset: '0',
  zIndex: '1400',
  isOpen: (el, s) => {
    const open = s.root?._isPhoneViewport !== false && !!s.root?.[el.stateKey]
    el.scope.syncRailFocus(el, open)
    return open
  },
  dataOpen: (el) => !!el.isOpen,
  ariaHidden: (el) => (el.isOpen ? 'false' : 'true'),
  // Visibility removes closed controls from keyboard navigation. The old
  // translated card still accepted Tab even when it ignored the pointer.
  visibility: 'hidden',
  '&[data-open]': { visibility: 'visible' },
  // Closed: the whole overlay ignores the pointer; open: it owns it.
  pointerEvents: (el) => (el.isOpen ? 'auto' : 'none'),
  dataMobileRail: (el) => el.stateKey || '',

  // Scrim — plain veil, tap closes. Opacity (compositor-friendly) only.
  // subscribeTo is per-ELEMENT: the root's subscription re-runs the root's own
  // factories, not its children's — Scrim and Card each subscribe themselves.
  Scrim: {
    position: 'absolute',
    inset: '0',
    // Adaptive (was fixed 'black.45') — the design system ships a
    // purpose-built token for exactly this ("Modal/sheet backdrops...
    // `scrimSoft` keeps it readable behind a bottom sheet", color.js).
    background: 'scrimSoft',
    subscribeTo: (el) => [el.parent.stateKey].filter(Boolean),
    opacity: (el, s) => (s.root?.[el.parent.stateKey] ? '1' : '0'),
    // The veil's motion in tokens (DESIGN_SPEC §6.4): it fades in on C and
    // out one letter faster, on B (was a raw 220ms both ways). The state
    // reaches the CSS as `data-open` + a static rule — a factory-valued
    // transition would be written inline and could not differ per direction.
    dataOpen: (el, s) => !!s.root?.[el.parent.stateKey],
    transition: 'opacity B defaultBezier',
    '&[data-open]': { transition: 'opacity C defaultBezier' },
    onClick: (e, el, s) => {
      s.root.update({ [el.parent.stateKey]: false }, { preventFetch: true })
    }
  },

  // Same opaque surface and geometry as AppDialog. Card.Content remains
  // stable so every existing page keeps its own rail content and state key.
  Card: {
    dataRailCard: true,
    role: 'dialog',
    ariaModal: 'true',
    ariaLabel: (el) => el.parent.railLabel || el.call('polyglot', 'shell.headerTabs.primaryNav'),
    tabindex: '-1',
    subscribeTo: (el) => [el.parent.stateKey].filter(Boolean),
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    width: 'overlaySm',
    // A strip of the page stays in view beside the card: the sheet reads as
    // a drawer over the page, and the scrim strip is a real tap target.
    maxWidth: 'calc(100% - C)',
    boxSizing: 'border-box',
    theme: 'dialog',
    backdropFilter: 'none',
    borderWidth: '0',
    round: '0 radiusSheet radiusSheet 0',
    flow: 'y',
    minWidth: '0',
    overflow: 'hidden',
    overscrollBehavior: 'contain',
    // Notch + home-indicator clearance INSIDE the card so content never
    // hides behind hardware; symmetric A side padding.
    //
    // LONGHANDS, deliberately. The atomic compiler resolves a value that is
    // EITHER a design-system token (`A`) or ONE self-contained raw expression
    // (`calc(env(…) + var(…))` hashes to its own class, same as the shell's
    // `calc(var(--spacing-X) * -1)` rules). A 4-value SHORTHAND that mixes the
    // two forms resolves to neither and empty-rules — the first cut of this
    // file shipped exactly that and the card rendered with padding:0 (caught
    // live at 390px, 2026-08-02). Longhands keep each value in a form the
    // compiler can classify.
    paddingTop: 'calc(env(safe-area-inset-top) + var(--spacing-A))',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + var(--spacing-A))',
    paddingLeft: 'calc(env(safe-area-inset-left) + var(--spacing-A))',
    paddingRight: 'A',
    transform: (el, s) => (s.root?.[el.parent.stateKey] ? 'translateX(0)' : 'translateX(-6.5%)'),
    // The side sheet's motion in tokens (§6.4): in on D along the spring the
    // PageSlot window uses, out on C (was a raw 260ms both ways).
    dataOpen: (el, s) => !!s.root?.[el.parent.stateKey],
    transition: 'transform C defaultBezier',
    '&[data-open]': { transition: 'transform D springBezier' },
    shadow: 'overlay',
    ':focus-visible': { boxShadow: 'var(--shadow-overlay)' },
    onKeydown: (e, el) => {
      if (!e.defaultPrevented) el.call('appDialogTrapTab', e)
    },

    // Grab affordance + label row — the sheet introduces itself, then the
    // consumer's Content takes over.
    Header: {
      flow: 'x',
      align: 'center space-between',
      minWidth: '0',
      flexShrink: '0',
      marginBottom: 'Z',
      Title: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        text: (el) =>
          el.parent.parent.parent.railLabel || el.call('polyglot', 'shell.headerTabs.primaryNav')
      },
      // THE house close X (brand CloseButton — F2, one X for every panel);
      // the coarse-pointer 44pt tap slop comes from its IconButton base.
      CloseButton: {
        onClick: (e, el, s) => {
          s.root.update({ [el.parent.parent.parent.stateKey]: false }, { preventFetch: true })
        }
      }
    },

    // Consumer content mounts here (childExtends/children supplied by the
    // app). minWidth 0 so nothing inside can force horizontal overflow.
    Content: {
      flow: 'y',
      minWidth: '0',
      minHeight: '0',
      flex: '1',
      overflow: 'auto',
      overscrollBehavior: 'contain'
    },

    // Close on left-flick over the open sheet — the mirror of the opening
    // pull, so the gesture is symmetric. FA503: declared handlers on THIS
    // element's own node, so DOMQL registers each once and removes it in
    // dispose(). The pre-fix shape armed them from `onRender` behind a
    // `node.__railFlickArmed` latch and never removed either one; the gesture
    // origin lived in a closure that the latch then pinned for the node's
    // whole life. It rides own element fields now (the `el._docClickGuard`
    // idiom already used across this package) — per instance, collected with
    // the element, never a global and never a signal.
    onPointerdown: (e, el) => {
      el.__railFlickX = e.clientX
      el.__railFlickY = e.clientY
    },
    onPointerup: (e, el) => {
      const sx = el.__railFlickX
      if (sx == null) return
      const dx = e.clientX - sx
      const dy = Math.abs(e.clientY - el.__railFlickY)
      el.__railFlickX = null
      if (dx < -56 && dy < 48) {
        el.getRootState().update({ [el.parent.stateKey]: false }, { preventFetch: true })
      }
    }
  },

  // Viewport watcher — publishes the phone breakpoint to root state so the
  // `hide` factory above stays correct across resizes/rotation.
  //
  // FA503 / Rule 64: was a MediaQueryList `change` listener latched on a
  // `window.__railViewportWatch` global — a global WRITE, never removed, and
  // it pinned the FIRST sheet's element for the life of the tab (a second
  // rail, or a re-mounted one, published through a dead element). `resize` is
  // the framework-owned window event that carries the same edge; the query
  // itself is still the source of truth, re-read on every call.
  onCreate: (el) => el.scope.publishRailViewport(el),
  onWindowResize: (ev, el) => el.scope.publishRailViewport(el),
  onRemove: (el) => el.scope.syncRailFocus(el, false),

  // Escape closes (hardware keyboards exist on phones too). Was a raw
  // `doc.addEventListener('keydown')` latched on `doc.__railEscArmed` with no
  // removal: the first sheet to render owned the only listener, kept its
  // element alive after unmount, and closed by ITS `stateKey` forever.
  onDocumentKeydown: (e, el) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    const key = el.stateKey
    const r = el.getRootState()
    if (!key || !r?.[key] || r._isPhoneViewport === false) return
    const layer =
      typeof el.context?.functions?.shellOverlayOpen === 'function'
        ? el.call('shellOverlayOpen')
        : null
    if (layer && layer !== 'mobileRail') return
    // A portal dialog can sit outside this rail, and an inline dialog can
    // sit inside it. The nearest dialog owns Escape in both cases.
    const target = e.target || (typeof document !== 'undefined' && document.activeElement)
    const dialog = target?.closest?.('[role="dialog"], [aria-modal="true"]')
    if (dialog && dialog !== el.Card?.node) return
    e.preventDefault?.()
    e.stopPropagation?.()
    r.update({ [key]: false }, { preventFetch: true })
  }
}
